import React, { useState } from "react";
import { withStyles } from "@material-ui/core/styles";
import { withRouter } from "react-router-dom";
import createStyles from "@material-ui/core/styles/createStyles";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import classNames from "classnames";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from '@material-ui/core/IconButton';
import FilterIcon from '@material-ui/icons/FilterList';
import Checkbox from "@material-ui/core/Checkbox";
import UiTableSortLabel from "./UiTableSortLabel";
import withContext from "../../tools/withContext";
import ContextType from "../../types/Context";
import useRouter from "../../tools/useRouter";
import { tableTheme, tableCheckboxModernStyle, tableCellEdgePadding } from "./tableTheme";

const styles = (theme: Theme) =>
  createStyles({
    header: {
      display: "grid",
      borderBottom: "solid 1px silver",
      minHeight: 48,
      flexShrink: 0,
    },
    headerModern: {
      borderBottom: `1px solid ${tableTheme.border}`,
      backgroundColor: tableTheme.headerBg,
      minHeight: 44,
      alignItems: "center",
    },
    root: {
      textAlign: "center",
      borderBottom: "none",
      backgroundColor: "inherit",
      padding: 0,
      color: "gray",
      paddingTop: theme.spacing.unit,
    },
    rootModern: {
      color: tableTheme.textPrimary,
      paddingTop: 0,
      padding: `0 ${tableCellEdgePadding}px`,
      fontSize: "0.8125rem",
      fontWeight: 600,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 44,
    },
    rootModernLeft: {
      justifyContent: "flex-start",
    },
    rootModernRight: {
      justifyContent: "flex-end",
    },
    alLeft: {
      textAlign: "left",
    },
    alRight: {
      textAlign: "right",
    },
    filter: {
      margin: "0 0 0 4px",
      padding: "2px"
    },
    modernCheckbox: tableCheckboxModernStyle,
  });

interface PropsTypes {
  orderBy: string;
  order: any;
  columns: any[];
  ctx: ContextType;
  onRequestSort: any;
  withCheckBox: boolean;
  numSelected?: number;
  rowCount?: number;
  numSelectedTotal?: number;
  onSelectAllClick?: any;
  classes: any;
  actionsCellSize: any;
  variant?: "default" | "modern";
}

const EnhancedTableHead = (props: PropsTypes) => {
  const {
    orderBy,
    order,
    columns,
    ctx: { user },
    onRequestSort,
    withCheckBox,
    onSelectAllClick,
    numSelected,
    rowCount,
    classes,
    numSelectedTotal,
    variant = "default",
  } = props;

  const isModern = variant === "modern";


  const [activeFilterId, setActiveFilterId] = useState<string | null>(null);
  const router = useRouter();


  const searchParsed = router.query;
  const userRole = user ? user.authorities[0] : null;
  const gridTemplateColumns = columns
    .filter(
      (r: any) =>
        !r.onlyAdmin || r.onlyRoles === null || r.onlyRoles.includes(userRole)
    )
    .map((row: any) => `${row.size}fr`)
    .join(" ");
    
  // HANDLERS /////////
  const createSortHandler = (property: string) => (event: any) => {
    onRequestSort(event, property);
  };

  const handleCloseFilteringDialog = () => setActiveFilterId(null);

  const handleOpenFilteringDialog = (filterId: string) => () =>
    setActiveFilterId(filterId);

  const renderFilterDialog = (row: any) => {
    const filterComponent = row.filterComponent || row.filterCompononant;
    if (!row.filter || !filterComponent) return null;
    const commonProps = {
      open: activeFilterId === row.id,
      onClose: handleCloseFilteringDialog,
    };

    if (React.isValidElement(filterComponent)) {
      return (
        // @ts-ignore
        React.cloneElement(filterComponent, commonProps)
      );
    }

    if (typeof filterComponent === "function") {
      return filterComponent(commonProps);
    }

    return null;
  };

  return (
    <>
    {columns.map((row: any, index: number) => (
      <React.Fragment key={`filter-dialog-${row.id || index}`}>
        {renderFilterDialog(row)}
      </React.Fragment>
    ))}
    <div
      className={classNames(classes.header, {
        [classes.headerModern]: isModern,
      })}
      style={{ gridTemplateColumns }}
    >
      {withCheckBox && typeof numSelected !== "undefined" && (
        <div
          className={classNames(isModern && classes.rootModern, {
            [classes.rootModernLeft]: isModern,
          })}
        >
          <Checkbox
            indeterminate={
              (rowCount && numSelected > 0 && numSelected < rowCount) ||
              (numSelected === 0 &&
                numSelectedTotal !== undefined &&
                numSelectedTotal > 0)
            }          
            checked={!rowCount ? false : numSelected === rowCount}
            disabled={!rowCount || rowCount === 0}
            onChange={onSelectAllClick}
            onClick={onSelectAllClick}
            classes={isModern ? { root: classes.modernCheckbox } : undefined}
          />
        </div>
      )}
      {columns
        .filter(
          (r: any) =>
            r.label !== "" &&
            (!r.onlyAdmin ||
              r.onlyRoles === null ||
              r.onlyRoles.includes(userRole))
        )
        .map((row: any, index: number) => (
          <div 
            key={index}
            // className={classes.root} 
            className={classNames(classes.root, {
              [classes.alLeft]: row.align === "left",
              [classes.alRight]: row.align === "right",
              [classes.rootModern]: isModern,
              [classes.rootModernLeft]: isModern && row.align === "left",
              [classes.rootModernRight]: isModern && row.align === "right",
            })}
          >
            {//@ts-ignore
            row.sortable ? (
              (() => {
                const sortKey = row.filterId || row.id;
                return (
              <Tooltip
                title="Sort"
                placement={row.numeric ? "bottom-end" : "bottom-start"}
                enterDelay={300}
                onClick={createSortHandler(sortKey)}
              >
                <UiTableSortLabel
                  active={orderBy === row.id || orderBy === row.filterId}
                  direction={order}
                >
                  {row.label}
                </UiTableSortLabel>
              </Tooltip>
                );
              })()
            ) : (
              <span>{row.label}</span>
            )}
            {row.filter && (
              (() => {
                const hasActiveFilter =
                  typeof row.filterActive === "function"
                    ? row.filterActive(searchParsed)
                    : !!row.filterActive;

                return (
              <IconButton 
                title="Filtrer"
                className={classes.filter}
                onClick={handleOpenFilteringDialog(row.id)}
              >
                <FilterIcon 
                  fontSize="small" 
                  color={hasActiveFilter ? "secondary" : "default"}
                />
              </IconButton>
                );
              })()
            )}
          </div>
        ))}
    </div>
    </>
  );
};

EnhancedTableHead.defaultProps = {
  withCheckBox: false,
  actionsCellSize: 2,
  variant: "default" as const,
};

export default withRouter(withContext(withStyles(styles)(EnhancedTableHead)));
